package com.arenaproplay.admin.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.di.LocalAppContainer
import com.arenaproplay.admin.domain.admin.AdminPerson
import com.arenaproplay.admin.navigation.Routes
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.admin.PlayerCard
import com.arenaproplay.admin.ui.components.admin.PlayerCardSize
import com.arenaproplay.admin.ui.layouts.AdminLayout
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodyLMedium
import com.arenaproplay.admin.ui.theme.bodyMMedium
import com.arenaproplay.admin.ui.theme.bodyMRegular
import com.arenaproplay.admin.ui.theme.bodySMedium
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.toRgbaString
import com.arenaproplay.admin.ui.utils.countryFlag
import com.arenaproplay.admin.ui.utils.sBorderRadius
import kotlin.js.Date
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

private const val PEOPLE_PAGE_SIZE = 24

// Виды спорта, известные SportColors — для выбора цвета карточки
private val KNOWN_SPORTS = setOf("football", "soccer", "tennis", "volleyball", "basketball", "padel")

// «N лет» с правильным склонением
private fun ageRu(age: Int): String {
    val mod10 = age % 10
    val mod100 = age % 100
    val word = when {
        mod100 in 11..14 -> "лет"
        mod10 == 1 -> "год"
        mod10 in 2..4 -> "года"
        else -> "лет"
    }
    return "$age $word"
}

private fun relativeRu(iso: String?): String? {
    if (iso.isNullOrBlank()) return null
    val ms = Date.parse(iso)
    if (ms.isNaN()) return null
    val diffSec = ((Date.now() - ms) / 1000).toLong()
    return when {
        diffSec < 60 -> "только что"
        diffSec < 3600 -> "был(а) ${diffSec / 60} мин назад"
        diffSec < 86_400 -> "был(а) ${diffSec / 3600} ч назад"
        diffSec < 2_592_000 -> "был(а) ${diffSec / 86_400} д назад"
        else -> null
    }
}

@Page("/people")
@Composable
fun PeoplePage() {
    val ctx = rememberPageContext()
    val repo = LocalAppContainer.current.adminRepository ?: return
    val scope = rememberCoroutineScope()

    var people by remember { mutableStateOf<List<AdminPerson>>(emptyList()) }
    var total by remember { mutableStateOf(0) }
    var page by remember { mutableStateOf(0) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var retryToken by remember { mutableStateOf(0) }

    // Юзеры, у которых фото удалено в рамках этой сессии (локальное обновление).
    val removedAvatarIds = remember { mutableStateSetOf<String>() }

    LaunchedEffect(page, retryToken) {
        if (page == 0) { loading = true; error = null }
        try {
            val result = repo.getPeople(page, PEOPLE_PAGE_SIZE)
            people = if (page == 0) result.items else people + result.items
            total = result.total
        } catch (e: Exception) {
            error = e.message ?: "Ошибка загрузки"
        } finally {
            loading = false
        }
    }

    AdminLayout(
        title = "Пипл",
        subtitle = if (total > 0) "$total полных профилей" else "Юзеры с заполненным профилем",
    ) {
        when {
            error != null && people.isEmpty() -> {
                GlassCard {
                    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                        SpanText("Не удалось загрузить: $error", Modifier.bodySRegular().color(AppColors.Error))
                        Box(Modifier.height(10.px))
                        NaActionButton("Повторить") { retryToken++ }
                    }
                }
            }
            loading && people.isEmpty() -> PeopleSkeletonGrid()
            people.isEmpty() -> EmptyState(
                icon = "contact",
                title = "Пока нет юзеров с полным профилем",
                hint = "Полный профиль = имя, аватар, дата рождения и интересы",
            )
            else -> {
                PeopleGrid(people, removedAvatarIds) { person ->
                    ctx.router.navigateTo("${Routes.USERS}?q=${js("encodeURIComponent")(person.name)}")
                }
                val shown = people.size
                if (shown < total) {
                    Box(Modifier.height(18.px))
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = com.varabyte.kobweb.compose.foundation.layout.Arrangement.Center) {
                        NaActionButton(if (loading) "Загрузка…" else "Показать ещё ($shown из $total)") {
                            if (!loading) { loading = true; scope.launch { page++ } }
                        }
                    }
                }
                Box(Modifier.height(28.px))
            }
        }
    }
}

@Composable
private fun PeopleGrid(
    people: List<AdminPerson>,
    removedAvatarIds: MutableSet<String>,
    onClick: (AdminPerson) -> Unit,
) {
    val repo = LocalAppContainer.current.adminRepository
    val scope = rememberCoroutineScope()

    // Состояние: для кого показываем оверлей выбора причины
    var moderatingPerson by remember { mutableStateOf<AdminPerson?>(null) }

    // Грид ТОЛЬКО на Div: kobweb-box складывает детей в одну ячейку.
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .classNames("na-card-grid")
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fill, minmax(210px, 1fr))")
                property("gap", "16px")
            }
            .toAttrs()
    ) {
        people.forEach { person ->
            PersonCard(
                person = person,
                avatarRemoved = person.id in removedAvatarIds,
                onModerationClick = { moderatingPerson = person },
                onClick = { onClick(person) },
            )
        }
    }

    // Диалог точечной модерации: все фото юзера → выбор → причина
    val target = moderatingPerson
    if (target != null && repo != null) {
        AvatarModerationDialog(
            userId = target.id,
            userName = target.name,
            repo = repo,
            onDismiss = { moderatingPerson = null },
            // Точное соответствие «что показано в гриде» неизвестно (грид не
            // несёт cardId) — после любого удаления прячем фото до перезагрузки.
            onRemoved = { removedAvatarIds.add(target.id) },
        )
    }
}

@Composable
private fun PersonCard(
    person: AdminPerson,
    avatarRemoved: Boolean,
    onModerationClick: () -> Unit,
    onClick: () -> Unit,
) {
    var imgFailed by remember(person.id) { mutableStateOf(false) }
    // Wrapper: position relative для ⋯ кнопки поверх карточки
    Div(
        attrs = Modifier
            .styleModifier { property("position", "relative") }
            .toAttrs()
    ) {
        Box(
            Modifier
                .cursor(Cursor.Pointer)
                .classNames("na-press", "arena-lift")
                .onClick { onClick() }
        ) {
            GlassCard(padding = 14) {
                // Аватар: настоящая карточка игрока, если есть card-поля; иначе — фото/инициалы
                // Карточка рендерится только при наличии рамки: замороженные
                // (аватар есть, шаблон обнулён подпиской) падают в обычное фото.
                val hasCard = !avatarRemoved && person.cardAvatarUrl != null &&
                    (person.cardAssetsBasePath != null || person.cardImageUrl != null)
                val showPhoto = !avatarRemoved && !imgFailed
                if (hasCard) {
                    // Слот под карточку: ширина = 100% ячейки, высота по соотношению 130:202 (~0.643)
                    Div(
                        attrs = Modifier
                            .fillMaxWidth()
                            .styleModifier {
                                property("aspect-ratio", "130 / 202")
                                property("overflow", "visible")
                            }
                            .toAttrs()
                    ) {
                        PlayerCard(
                            playerName = person.name,
                            sport = person.interests.firstOrNull { it.lowercase() in KNOWN_SPORTS } ?: "football",
                            size = PlayerCardSize.Medium,
                            avatarUrl = person.cardAvatarUrl,
                            cardAssetsBasePath = person.cardAssetsBasePath,
                            cardTextColor = person.cardTextColor,
                            cardFrameUrl = person.cardImageUrl ?: "",
                            hideRating = true,
                            fillContainer = true,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                } else if (showPhoto) {
                    Div(
                        attrs = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(12)
                            .styleModifier {
                                property("aspect-ratio", "1 / 1")
                                property("overflow", "hidden")
                                property("background", "rgba(255,255,255,.04)")
                            }
                            .toAttrs()
                    ) {
                        Img(
                            src = person.avatarUrl,
                            attrs = {
                                attr("loading", "lazy")
                                attr("alt", person.name)
                                style {
                                    property("width", "100%")
                                    property("height", "100%")
                                    property("object-fit", "cover")
                                    property("display", "block")
                                }
                                // compose-web Img attrs lack a typed onError — raw listener
                                addEventListener("error") { imgFailed = true }
                            }
                        )
                    }
                } else {
                    Box(
                        Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "1 / 1") },
                        contentAlignment = Alignment.Center,
                    ) {
                        AvatarBubble(
                            seed = person.id,
                            initials = person.name.trim().split(" ").filter { it.isNotEmpty() }
                                .take(2).mapNotNull { it.firstOrNull()?.uppercaseChar() }.joinToString(""),
                            sizePx = 72,
                        )
                    }
                }
                Box(Modifier.height(10.px))
                SpanText(
                    person.name,
                    Modifier.bodyLMedium().color(AppColors.Ink).styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("display", "block")
                    }
                )
                // «26 лет · 🇺🇦 Київ»
                val geoBits = buildList {
                    add(ageRu(person.age))
                    val flag = person.country?.let { countryFlag(it) } ?: ""
                    add("$flag ${person.city}".trim())
                }
                SpanText(
                    geoBits.joinToString(" · "),
                    Modifier.bodySRegular().color(AppColors.Muted).styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                        property("display", "block")
                        property("margin-top", "2px")
                    }
                )
                // Чипы интересов (≤4 + «+N»)
                if (person.interests.isNotEmpty()) {
                    Box(Modifier.height(8.px))
                    Div(
                        attrs = Modifier.fillMaxWidth().styleModifier {
                            property("display", "flex")
                            property("flex-wrap", "wrap")
                            property("gap", "5px")
                        }.toAttrs()
                    ) {
                        person.interests.take(4).forEach { InterestChip(it) }
                        val more = person.interests.size - 4
                        if (more > 0) InterestChip("+$more")
                    }
                }
                val rel = relativeRu(person.lastActive)
                if (rel != null) {
                    Box(Modifier.height(8.px))
                    SpanText(rel, Modifier.eyebrow().color(AppColors.Faint))
                }
            }
        }

        // ⋯ кнопка модерации — поверх карточки, правый верхний угол
        // Низкая непрозрачность (всегда видна), на наведении — полная.
        Box(
            Modifier
                .position(Position.Absolute)
                .top(8.px).right(8.px)
                .sBorderRadius(999)
                .cursor(Cursor.Pointer)
                .classNames("na-press", "avatar-mod-btn")
                .styleModifier {
                    property("background", "rgba(10,12,20,.60)")
                    property("border", "1px solid rgba(255,255,255,.12)")
                    property("padding", "4px 8px")
                    property("opacity", "0.45")
                    property("transition", "opacity 160ms ease, background 160ms ease")
                    property("z-index", "10")
                    property("line-height", "1")
                    property("font-size", "14px")
                    property("color", "rgba(240,244,252,.90)")
                    property("user-select", "none")
                }
                .onClick { event ->
                    event.stopPropagation()
                    onModerationClick()
                }
        ) {
            SpanText(
                "⋯",
                Modifier.styleModifier {
                    property("font-size", "14px")
                    property("color", "rgba(240,244,252,.90)")
                    property("line-height", "1")
                }
            )
        }
    }
}

@Composable
private fun InterestChip(text: String) {
    Box(
        Modifier.sBorderRadius(999).styleModifier {
            property("background", "rgba(255,255,255,.06)")
            property("border", "1px solid rgba(255,255,255,.09)")
            property("padding", "3px 9px")
        }
    ) {
        SpanText(
            text,
            Modifier.styleModifier {
                property("font-size", "11px")
                property("color", "rgba(240,244,252,.75)")
                property("white-space", "nowrap")
            }
        )
    }
}

@Composable
private fun NaActionButton(label: String, onClick: () -> Unit) {
    Box(
        Modifier
            .sBorderRadius(10)
            .cursor(Cursor.Pointer)
            .classNames("na-press")
            .styleModifier {
                property("background", "rgba(0,97,255,.22)")
                property("border", "1px solid rgba(0,97,255,.45)")
                property("padding", "9px 18px")
            }
            .onClick { onClick() }
    ) {
        SpanText(label, Modifier.bodySMedium().color(AppColors.PrimaryLight))
    }
}

@Composable
private fun PeopleSkeletonGrid() {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .classNames("na-card-grid")
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fill, minmax(210px, 1fr))")
                property("gap", "16px")
            }
            .toAttrs()
    ) {
        repeat(6) {
            Div(
                attrs = Modifier
                    .sBorderRadius(16)
                    .styleModifier {
                        // Пропорция карточки игрока 130:202 ≈ 0.643 (ближе к 2:3 чем 3:4)
                        property("aspect-ratio", "130 / 202")
                        property("background", "rgba(255,255,255,.04)")
                        property("animation", "admin-skeleton 1.4s ease-in-out infinite")
                    }
                    .toAttrs()
            ) {}
        }
    }
}
